import React, { useContext } from "react";
import { FaCheckCircle, FaLightbulb, FaHandsHelping, FaBullhorn } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import "../TrainingGlobal.css";
import BannerImage from "./iso.jpg";

const HIRA: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  const handleBooking = () => {
    if (isAuthenticated) {
      navigate('/booking/hira');
    } else {
      navigate('/login', { state: { from: '/booking/hira' } });
    }
  };

  return (
    <div className="training-page">
      <div className="training-max-width">

        {/* --- HERO --- */}
        <div className="training-hero" style={{ backgroundImage: `url(${BannerImage})` }}>
          <span className="hero-tag">ISO Training</span>
          <h1 className="hero-title">HIRA and Control Methods</h1>
          <p className="hero-subtitle">
            Ensuring hazard identification and risk management
          </p>
          <button onClick={handleBooking} className="btn-hero">
            Book Now <span style={{ fontSize: '1.2em' }}>→</span>
          </button>
        </div>

        {/* --- CONTENT --- */}
        <div className="detail-content">

          <div className="content-article">
            <div className="article-section">
              <h2 className="article-heading">Identification of Hazard</h2>
              <p className="article-text">
                Hazard identification involves recognizing potential sources of harm in the workplace. This step is critical for preventing accidents and ensuring employee safety. Hazards can include physical, chemical, biological, or ergonomic risks. Conducting regular inspections and engaging employees in hazard identification ensures a comprehensive approach. Proactively identifying hazards reduces the likelihood of incidents and fosters a culture of safety.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Exposure to Hazard</h2>
              <p className="article-text">
                Exposure to hazards occurs when workers come into contact with dangerous conditions or substances. Assessing exposure levels helps in implementing effective control measures. For example, prolonged exposure to loud noise can lead to hearing loss, while contact with toxic chemicals can cause health issues. Monitoring exposure levels and providing appropriate protective equipment minimize risks and ensure worker safety.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Routine and Non-routine Activity</h2>
              <p className="article-text">
                Routine activities are regular tasks performed daily, while non-routine activities include maintenance, repairs, or emergency operations. Both require hazard assessments to ensure safety. Non-routine activities often involve unique risks, such as working in confined spaces or handling hazardous materials. Identifying and addressing these risks through proper planning and training ensures a safe working environment for all tasks.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Event and Consequence</h2>
              <p className="article-text">
                Understanding the potential events and their consequences helps in evaluating risks and planning mitigation strategies to minimize impacts. For example, a chemical spill could lead to environmental contamination and health risks. Analyzing the chain of events and their outcomes enables organizations to implement preventive measures and emergency response plans effectively. This approach minimizes the impact of incidents on workers and operations.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">HIRA Methodology</h2>
              <p className="article-text">
                The HIRA methodology involves identifying hazards, assessing risks, and implementing controls. It is a systematic approach to managing workplace safety. This methodology includes steps such as hazard identification, risk evaluation, and prioritization of control measures. Regularly reviewing and updating the HIRA process ensures that emerging risks are addressed promptly. A robust HIRA methodology enhances organizational resilience and safety.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Risk Rating Matrix</h2>
              <p className="article-text">
                A risk rating matrix evaluates the likelihood and severity of risks. It helps prioritize hazards and allocate resources effectively. For example, a high-likelihood, high-severity risk requires immediate attention, while a low-likelihood, low-severity risk may require periodic monitoring. Using a risk rating matrix ensures a structured approach to risk management and enhances decision-making.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Criteria for Likelihood and Severity</h2>
              <p className="article-text">
                Likelihood refers to the probability of an event occurring, while severity measures its potential impact. Both criteria are essential for risk assessment. For example, a highly likely event with severe consequences requires urgent mitigation. Defining clear criteria for likelihood and severity ensures consistency in risk evaluations and helps organizations focus on critical risks.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Risk Priority Number</h2>
              <p className="article-text">
                The Risk Priority Number (RPN) is calculated by multiplying likelihood, severity, and detection ratings. It helps in identifying high-priority risks. For example, an RPN of 100 indicates a critical risk that requires immediate action, while a lower RPN may indicate a less urgent issue. Using RPN ensures a quantitative approach to risk prioritization and resource allocation.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Assessment of Risk</h2>
              <p className="article-text">
                Risk assessment involves analyzing hazards, evaluating their impact, and determining appropriate control measures to mitigate risks. This process includes identifying who might be harmed, how, and the likelihood of occurrence. Regularly updating risk assessments ensures that new hazards are addressed promptly. A thorough risk assessment is the foundation of effective safety management.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Risk Management</h2>
              <p className="article-text">
                Risk management includes implementing controls, monitoring their effectiveness, and continuously improving safety measures to reduce risks. Controls can include engineering solutions, administrative policies, and personal protective equipment. Regularly reviewing and updating risk management strategies ensures their relevance and effectiveness. A proactive approach to risk management enhances workplace safety and compliance.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Hazard Identification Tools</h2>
              <p className="article-text">
                Tools such as checklists, job safety analysis, and incident reports assist in identifying hazards and improving workplace safety. For example, checklists ensure that no potential hazards are overlooked during inspections. Job safety analysis breaks down tasks to identify risks at each step. Using these tools enhances the accuracy and efficiency of hazard identification processes.
              </p>
            </div>

            <div className="article-section">
              <h2 className="article-heading">Change Management</h2>
              <p className="article-text">
                Change management ensures that safety measures are updated to address new hazards arising from changes in processes, equipment, or materials. For example, introducing new machinery may require updated training and risk assessments. A structured change management process minimizes disruptions and ensures that safety remains a priority during transitions. This approach enhances adaptability and resilience in dynamic work environments.
              </p>
            </div>
          </div>

          {/* --- SIDEBAR --- */}
          <div className="content-sidebar">
            <div className="sidebar-widget">
              <h4 className="widget-title">Key Elements</h4>
              <ul className="key-list">
                <li><FaCheckCircle className="check-icon" /> Hazard Identification</li>
                <li><FaLightbulb className="check-icon" /> Risk Assessment</li>
                <li><FaHandsHelping className="check-icon" /> Risk Matrix</li>
                <li><FaBullhorn className="check-icon" /> Change Management</li>
              </ul>
            </div>

            <div className="sidebar-widget">
              <h4 className="widget-title">Why HIRA?</h4>
              <p style={{ color: '#aaa', lineHeight: '1.6', fontSize: '0.9rem' }}>
                Systematically identify and mitigate risks to ensure a safe workplace.
              </p>
            </div>

            <div className="sidebar-widget" style={{ background: 'linear-gradient(135deg, #111 0%, #222 100%)', border: '1px solid #333' }}>
              <h4 className="widget-title">Get Certified</h4>
              <p style={{ color: '#ccc', marginBottom: '20px', fontSize: '0.9rem' }}>
                Start your journey towards standard compliance.
              </p>

              <button
                onClick={handleBooking}
                className="btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Book Now
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default HIRA;

